import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActionItem, ModalActionEnum } from '@dialogs/actions-panel/actions-panel.component';
import { CreateProductDialogComponent } from '@dialogs/create-product-dialog/create-product-dialog.component';
import { GridOverrides, NewUiRoute, RowSelection } from '@mds/common';
import { MdsIcon } from '@mds/icons';
import { GridModule, GridNoRowsComponent } from '@mds/ui';
import { ComposerService } from '@services/composer.service';
import { ComposerApiService } from '@services/composer-api.service';
import { MenuService } from '@services/menu.service';
import { ToastrService } from '@services/toaster.service';
import { CellValueChangedEvent, GridApi, SortModelItem } from 'ag-grid-community';
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { cloneDeep } from 'lodash';
import { forkJoin } from 'rxjs';

import { buildColumnDefs } from './utils/composer-column.factory';
import { mapFilterModel as buildFilterCriteria } from './utils/composer-filter.mapper';
import { ComposerDialogService } from './utils/composer-dialog.service';
import { ComposerRowData, ComposerTabData, FilterCriteria, OrderingCriteria, PaginatedComposerResponse } from './utils/composer-grid.models';
import { buildPayloads } from './utils/composer-payload.builder';
import { mapRows } from './utils/composer-row.mapper';
import { ComposerDataService } from '../../services/composer-data.service';

const FORECAST_ACTIONS_TAB_KEY = 'in_contributes_to';

@Component({
  selector: 'app-composer-grid',
  standalone: true,
  imports: [GridModule],
  templateUrl: './composer-grid.component.html',
  providers: [ComposerApiService, ComposerDialogService],
  styleUrls: ['./composer-grid.component.scss']
})
export class ComposerGridComponent implements OnChanges, OnDestroy {
  @Input() tabData: any;
  @Input() paginationMeta: { page: number; limit: number; total_count: number; has_more: boolean };
  @Input() showLoading = false;
  @Output() reloadData = new EventEmitter<void>();
  @Output() selectedRow = new EventEmitter<any>();
  @Output() actionToPerform = new EventEmitter<ActionItem[]>();

  overrides: GridOverrides;
  rowData: ComposerRowData[] = [];
  availableActions: ActionItem[] = [];
  selectedRows: any[] = [];
  inlineEditRow: string[] = [];
  loadNewPage: boolean;
  pageNo: number;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly composerApiService = inject(ComposerApiService);
  private readonly toastrService = inject(ToastrService);
  private readonly composerService = inject(ComposerService);
  private readonly menuService = inject(MenuService);
  private readonly dialogService = inject(ComposerDialogService);

  private gridApi: GridApi;
  private data: ComposerTabData;
  private selectedRowData: any;
  private currentColumnKey: string;
  private datasourceVersion = 0;

  constructor() {
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
      if (action?.key === 'save-grid') {
        this.saveUpdatedGrid();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showLoading && this.gridApi) {
      this.gridApi.setGridOption('loading', this.showLoading);
    }

    if (!changes.tabData || !this.tabData) return;

    const isServerPaginated = !!this.paginationMeta;
    this.data = isServerPaginated ? this.tabData : cloneDeep(this.tabData);

    const prevLineCount = this.rowData.length;
    const newLineCount = this.data.lines?.length ?? 0;
    const emptyStateChanged = (prevLineCount === 0) !== (newLineCount === 0);

    if (this.gridApi && this.overrides && isServerPaginated && !emptyStateChanged) {
      this.updateGridInPlace();
    } else {
      this.overrides = undefined;
      this.createGridData();
    }

    if (changes?.tabData?.previousValue?.name !== changes?.tabData?.currentValue?.name) {
      this.selectedRows = [];
    }
  }

  ngOnDestroy(): void {
    this.dialogService.closeAll();
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
    if (this.gridApi) {
      setTimeout(() => {
        if (!this.paginationMeta) {
          gridApi?.resetRowHeights();
        }
        gridApi?.refreshHeader();
      }, 0);
    }
  }

  onSelectionChanged(): void {
    this.selectedRows = this.gridApi.getSelectedRows();
    if (this.selectedRows.length > 0) {
      if (this.availableActions.length === 1) {
        this.availableActions.unshift({
          label: 'Delete',
          icon: MdsIcon.PROFILE_TRASH,
          value: ModalActionEnum.DELETE,
          callback: () => this.deleteConfirmation()
        });
        this.actionToPerform.emit(this.availableActions);
      }
    } else {
      this.activateActionModal();
    }
  }

  onCellClicked(ev: any): void {
    if (!ev.data || ev.colDef?.field === 'actions') return;

    const linkKey = ev.data[`Link_${ev.column.colId}`] || ev.data[`Link_${ev.colDef.headerName}`];
    if (linkKey && !ev.data.isEditing) {
      const updatedUrl = new NewUiRoute().transform(linkKey.url, true);
      window.open(updatedUrl, '_blank');
    }

    if (this.selectedRowData !== ev.data) {
      this.selectedRowData = ev.data;
      this.selectedRow.emit(this.selectedRowData);
    }
  }

  onCellValueChanged(ev: CellValueChangedEvent): void {
    if (ev.newValue === ev.oldValue) return;

    const colId = ev.column.getColId();
    const tempId = ev.data.rid || ev.data.in || ev.data.out;

    if (!this.inlineEditRow.includes(tempId)) {
      ev.data.edited = true;
      this.inlineEditRow.push(tempId);
    }

    if (this.inlineEditRow.length > 0) {
      this.composerService.hasUnsavedChanges = true;
      this.updateMenuActions({ 'save-grid': true });
    } else {
      this.composerService.hasUnsavedChanges = false;
      this.updateMenuActions({ 'save-grid': true, hidden: true });
    }

    ev.data.row.forEach((d) => {
      d.key = d.key.replace('$', '.');
      const key = d.key.split('.').pop();
      if (key === colId) {
        d.value = ev.newValue;
        d.display_value = ev.newValue;
        if (!ev.data?.editedCell.includes(key)) {
          ev.data.editedCell.push(key);
        }
      }
    });
  }

  getNextPageData(): void {
    this.pageNo++;
    const edge = this.data.lines[0].find((e) => e.key.endsWith('.rid'));
    const subModule = edge.rid || edge.value;
    this.composerApiService.showAll(this.data.targetRid, subModule, this.pageNo).subscribe((res: any) => {
      if (res.lines?.length > 0) {
        this.loadNewPage = true;
        this.reloadComposer();
      }
    });
  }

  private createGridData(): void {
    this.rowData = mapRows(this.data.lines, this.data.formSchema, this.data.actions);
    const { edit, delete: canDelete } = this.data.actions;
    const hasAnyAction = edit || canDelete;
    const columnDefs = buildColumnDefs(
      this.data.lines,
      this.data.formSchema,
      !!edit,
      (params) => this.singleRowEvent(params),
      !!hasAnyAction
    );

    this.currentColumnKey = this.data.formSchema?.map((f: any) => f.key).join(',') ?? '';
    const useServerPagination = !!this.paginationMeta;
    this.loadNewPage = true;

    const defaultColDef: any = {
      lockPosition: true,
      lockPinned: true,
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      flex: 1
    };
    if (!useServerPagination) {
      defaultColDef.headerCheckboxSelectionFilteredOnly = true;
    }

    const baseOverrides: GridOverrides = {
      columnDefs,
      defaultColDef,
      sideBar: {
        toolPanels: [
          { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' }
        ]
      },
      suppressRowClickSelection: true,
      rowSelection: RowSelection.MULTIPLE,
      noRowsOverlayComponent: GridNoRowsComponent,
      pagination: true,
      noRowsOverlayComponentParams: {
        title: 'No data to show the table',
        description: 'Add data and it will start showing rows here'
      }
    };

    if (useServerPagination) {
      const pageSize = this.paginationMeta.limit;
      this.overrides = {
        ...baseOverrides,
        rowModelType: 'serverSide',
        dataSource: this.createServerSideDatasource(),
        paginationPageSize: pageSize,
        cacheBlockSize: pageSize,
        paginationPageSizeSelector: [10, 20, 50, 100],
        paginationAutoPageSize: false,
        groupDefaultExpanded: 0
      };
    } else {
      this.overrides = {
        ...baseOverrides,
        rowData: this.rowData,
        paginationAutoPageSize: true
      };
    }

    this.dialogService.closeAll();
    this.activateActionModal();
  }

  private updateGridInPlace(): void {
    this.rowData = mapRows(this.data.lines, this.data.formSchema, this.data.actions);

    const columnKey = this.data.formSchema?.map((f: any) => f.key).join(',') ?? '';
    const columnsChanged = columnKey !== this.currentColumnKey;

    if (columnsChanged) {
      const columnDefs = buildColumnDefs(
        this.data.lines,
        this.data.formSchema,
        !!this.data.actions.edit,
        (params) => this.singleRowEvent(params)
      );
      this.gridApi.setGridOption('columnDefs', columnDefs);
      this.currentColumnKey = columnKey;
    }

    this.gridApi.hideOverlay();
    this.gridApi.setGridOption('serverSideDatasource', this.createServerSideDatasource());
    this.gridApi.refreshServerSide({ purge: true });
    this.activateActionModal();
  }

  private createServerSideDatasource(): IServerSideDatasource {
    const composerDataService: ComposerDataService = this.data._composerDataService;
    const rid: string = this.data.targetRid;
    const tabKey: string = this.data._tabKey;
    const firstPageRows = this.rowData;
    const totalCount = this.paginationMeta.total_count;
    const version = ++this.datasourceVersion;
    let firstPageServed = false;

    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const startRow = params.request.startRow ?? 0;
        const endRow = params.request.endRow ?? startRow + this.paginationMeta.limit;
        const pageSize = endRow - startRow;

        const filters = this.mapFilterModel(params.request.filterModel);
        const ordering = this.mapSortModel(params.request.sortModel);
        const hasFiltersOrSorting = filters.length > 0 || ordering.length > 0;

        if (startRow === 0 && !firstPageServed && !hasFiltersOrSorting) {
          firstPageServed = true;
          params.success({ rowData: firstPageRows, rowCount: totalCount });
          if (totalCount === 0) {
            setTimeout(() => {
              if (version === this.datasourceVersion) {
                this.gridApi?.showNoRowsOverlay();
              }
            });
          } else {
            this.gridApi?.hideOverlay();
          }
          return;
        }

        const page = Math.floor(startRow / pageSize) + 1;
        composerDataService
          .getComposerTabData(rid, tabKey, page, pageSize, filters.length ? filters : undefined, ordering.length ? ordering : undefined)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: PaginatedComposerResponse) => {
              const tabItem = response.items[0];
              if (!tabItem) {
                params.success({ rowData: [], rowCount: startRow });
                if (startRow === 0 && version === this.datasourceVersion) {
                  this.gridApi?.showNoRowsOverlay();
                }
                return;
              }
              const rows = mapRows(tabItem.lines, tabItem.formSchema ?? this.data.formSchema, tabItem.actions ?? this.data.actions);
              const lastRow = response.has_more ? -1 : startRow + rows.length;
              params.success({ rowData: rows, rowCount: lastRow });
              if (startRow === 0) {
                this.gridApi?.hideOverlay();
              }
            },
            error: () => {
              params.fail();
            }
          });
      }
    };
  }

  private mapFilterModel(filterModel: Record<string, any> | undefined): FilterCriteria[] {
    const columnDefs: ColDef[] = (this.overrides?.columnDefs as ColDef[]) || [];
    return buildFilterCriteria(filterModel, columnDefs);
  }

  private mapSortModel(sortModel: SortModelItem[] | undefined): OrderingCriteria[] {
    if (!sortModel?.length) return [];
    return sortModel.map((s) => ({ field: s.colId, order: s.sort }));
  }

  private singleRowEvent(params: { event: ModalActionEnum; node: any }): void {
    if (params.event === ModalActionEnum.DELETE) {
      this.dialogService.deleteSingle(
        params.node.data,
        this.tabData.name,
        this.data.targetRid,
        this.inlineEditRow,
        (updated) => {
          this.inlineEditRow = updated;
          if (updated.length === 0) {
            this.composerService.hasUnsavedChanges = false;
            this.updateMenuActions({ 'save-grid': true, hidden: true });
          }
        },
        () => this.reloadComposer()
      );
    } else if (params.event === ModalActionEnum.EDIT) {
      this.editRow(params);
    } else if (params.event === ModalActionEnum.VIEW) {
      this.dialogService.navigateToEdit(params.node.data.row);
    }
  }

  private editRow(ev: any): void {
    if (ev.node.data.edited) {
      this.dialogService.saveChangesWarningDialog(() => {
        this.composerService.hasUnsavedChanges = false;
        this.openEditDialog(ev.node.data.originalData.row);
      });
    } else {
      this.openEditDialog(ev.node.data.row);
    }
  }

  private openEditDialog(lines: any[]): void {
    this.dialogService.openEditDialog(
      this.data.formSchema,
      lines,
      this.data.targetRid,
      this.data.layer,
      () => this.reloadComposer()
    );
  }

  private activateActionModal(): void {
    this.availableActions = [];
    if (this.tabData.actions.new) {
      this.availableActions.push({
        label: `Add ${this.tabData.name ?? ''}`,
        icon: MdsIcon.PLUS_DARK,
        value: ModalActionEnum.ADD,
        callback: () => this.openEditDialog([])
      });

      if (this.data?._tabKey === FORECAST_ACTIONS_TAB_KEY) {
        this.availableActions.push({
          label: 'New Forecast Action',
          icon: MdsIcon.PLUS_DARK,
          value: ModalActionEnum.SAVE,
          callback: () => this.openCreateForecastActionDialog()
        });
      }
    }

    this.actionToPerform.emit(this.availableActions);
  }

  private openCreateForecastActionDialog(): void {
    this.dialog.open(CreateProductDialogComponent, {
      autoFocus: false,
      width: '60vw',
      data: {
        dataCard: {
          tool: 'composer/new/scenario_action',
          app_name: 'Forecast Action'
        }
      }
    });
  }

  private deleteConfirmation(): void {
    const rids = this.selectedRows.map((e) => e.rid);
    this.dialogService.deleteMultiple(rids, this.tabData.name, this.data.targetRid, () => {
      this.selectedRows = [];
      this.availableActions.shift();
      this.actionToPerform.emit(this.availableActions);
      this.reloadComposer();
    });
  }

  private saveUpdatedGrid(): void {
    const payloads = buildPayloads(this.rowData, this.data.formSchema);
    if (!payloads.length) return;

    const requests = payloads.map((payload) =>
      this.composerApiService.submitEditedMetadata(this.data.targetRid, payload)
    );
    forkJoin(requests).subscribe(() => {
      this.toastrService.showMessage('Updated Successfully');
      this.selectedRows = [];
      this.inlineEditRow = [];
      this.composerService.hasUnsavedChanges = false;
      this.updateMenuActions({ 'save-grid': true, hidden: true });
      this.reloadComposer();
    });
  }

  private updateMenuActions(obj: Record<string, any>): void {
    const key = Object.keys(obj).find((k) => k !== 'hidden');
    if (key) {
      this.menuService.updateMenuItem(key, { visible: !obj.hidden });
    }
  }

  private reloadComposer(): void {
    this.reloadData.emit();
  }
}
